// Nome do arquivo: DrawerHearder.tsx

import React, { useState, useEffect } from 'react';
import { useAuth } from '@context/auth';
import { Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';

// Importações do Material-UI v4
import {
  makeStyles,
  createTheme,
  ThemeProvider,
} from '@material-ui/core/styles';
import {
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Box,
  CssBaseline,
  Collapse,
  Menu,
  MenuItem,
  Badge,
} from '@material-ui/core';

// Importações de Ícones
import {
  Menu as MenuIcon,
  ChevronLeft,
  ExpandLess,
  ExpandMore,
  Description,
  Group,
  SupervisorAccount,
  Apartment,
  Pages,
  Person,
  FindInPage,
  MonetizationOn,
  AssignmentTurnedIn,
  BusinessCenter,
  AssignmentInd,
  CompareArrows,
  AccountBalance,
  Ballot,
  ChatBubble,
  Notifications,
} from '@material-ui/icons';
import {
  TestTube,
  Buildings,
  Calendar,
  FirstAidKit,
  UserPlus,
  MagnifyingGlass,
} from '@phosphor-icons/react';
import EsocialIcon from '@assets/esocial.svg';
import prancheta3 from '@assets/prancheta3.png';

// Importações de Rotas, Armazenamento e Componentes
import Router from '@routes/app.routes';
import {
  storageMenuCollapsGet,
  storageMenuCollapse,
} from '@base/storage/storageMenuCollapse';
import { Button } from '@components/Button';

// Definição do tipo para o estado dos menus expansíveis
interface StorageMenuCollapseProps {
  schedule: boolean;
  aso: boolean;
  enterpriseRh: boolean;
  financeiro: boolean;
  contratos: boolean;
}

// --- ESTILOS ---
const drawerWidth = 260;
const theme = createTheme({
  palette: {
    primary: { main: '#008080' },
    secondary: { main: '#f50057' },
    background: { default: '#f4f6f8' },
  },
  typography: {
    fontFamily: 'Roboto, sans-serif',
  },
});

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor: '#FFF',
    color: '#333',
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  drawerOpen: {
    width: drawerWidth,
    backgroundColor: theme.palette.primary.main,
    color: '#FFF',
    borderRight: 'none',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerClose: {
    backgroundColor: theme.palette.primary.main,
    color: '#FFF',
    borderRight: 'none',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: theme.spacing(7) + 1,
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(9) + 1,
    },
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  hide: {
    display: 'none',
  },
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    margin: theme.spacing(3, 0),
  },
  logo: {
    transition: 'width 0.3s',
  },
  headerIcons: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginLeft: 'auto',
  },
  listItemIcon: {
    minWidth: 40,
    color: '#FFF',
  },
  subItem: {
    paddingLeft: theme.spacing(4),
  },
  selectedItem: {
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
  },
  userButton: {
    textTransform: 'none',
    color: '#333',
  },
}));

// --- COMPONENTE PRINCIPAL ---
export const DrawerHearder: React.FC = () => {
  const classes = useStyles();
  const { signOut, user } = useAuth();
  const [open, setOpen] = useState(true);
  const [openCollapse, setOpenCollapse] = useState<StorageMenuCollapseProps>({
    schedule: false,
    aso: false,
    enterpriseRh: false,
    financeiro: false,
    contratos: false,
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const location = useLocation();

  // --- DADOS DO MENU ---
  const menuItems = [
    {
      text: 'Agendamento',
      icon: <Calendar />,
      name: 'schedule',
      subItems: [
        {
          text: 'Cadastrar',
          icon: <UserPlus size={24} />,
          link: '/agendar',
          state: () => ({ status: open }),
        },
        { text: 'Consultar', icon: <MagnifyingGlass size={24} />, link: '/agendamentos' },
        { text: 'Pré-Agendamento', icon: <MagnifyingGlass size={24} />, link: '/agendamentos-wp' },
      ],
    },
    {
      text: 'ASO',
      icon: <FirstAidKit size={24} />,
      name: 'aso',
      subItems: [
        { text: 'Exames a vencer', icon: <TestTube size={24} />, link: '/exames-a-vencer' },
        { text: 'Kit', icon: <FindInPage />, link: '/kit' },
        { text: 'E-social', icon: <img src={EsocialIcon} width={24} height={24} />, link: '/e-social' },
      ],
    },
    { text: 'Credenciados', icon: <Description />, link: '/credenciados' },
    { text: 'Colaboradores', icon: <Person />, link: '/colaboradores' },
    {
      text: 'Empresas-RH',
      icon: <Group />,
      name: 'enterpriseRh',
      subItems: [
        { text: 'Empresa', icon: <Buildings size={24} />, link: '/empresa-rh' },
        { text: 'Local/Obra', icon: <Apartment />, link: '/local-obra' },
        { text: 'GHE', icon: <Pages />, link: '/ghe' },
      ],
    },
    {
      text: 'Financeiro',
      icon: <MonetizationOn />,
      name: 'financeiro',
      subItems: [
        { text: 'Exames Emp.', icon: <BusinessCenter />, link: '/exames-empresas' },
        { text: 'Exames Cred.', icon: <AssignmentInd />, link: '/exames-credenciados' },
        { text: 'Comparativo', icon: <CompareArrows />, link: '/comparativo-exames' },
      ],
    },
    {
      text: 'Contratos',
      icon: <AssignmentTurnedIn />,
      name: 'contratos',
      subItems: [
        { text: 'Atualizar Cont-Emp.', icon: <AccountBalance />, link: '/atua-empresas' },
        { text: 'Contratos Emp Ativos', icon: <AccountBalance />, link: '/contratos-empresa' },
        { text: 'Contratos Emp Inativos', icon: <AccountBalance />, link: '/contratos-empresa-inativos'  },
        { text: 'Contratos Cred', icon: <Ballot />, link: '/contratos-credenciado' },
      ],
    },
    {
      text: 'Administrativo',
      icon: <SupervisorAccount />,
      link: '/adm',
      adminOnly: true,
    },
  ];

  useEffect(() => {
    const loadMenuState = async () => {
      const storedState = await storageMenuCollapsGet();
      if (storedState) {
        setOpenCollapse(prevState => ({ ...prevState, ...storedState }));
      }
    };
    loadMenuState();
  }, []);

  const handleToggleDrawer = () => {
    setOpen(!open);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCollapseClick = (name: keyof StorageMenuCollapseProps) => {
    const newState = { ...openCollapse, [name]: !openCollapse[name] };
    setOpenCollapse(newState);
    storageMenuCollapse(newState);
    if (!open) setOpen(true);
  };

  const renderMenuItem = (item: any, isSubItem = false) => {
    if (item.adminOnly && user.administrador !== 'sim') {
      return null;
    }

    const isSelected = location.pathname === item.link;

    if (item.subItems) {
      return (
        <React.Fragment key={item.name}>
          <ListItem button onClick={() => handleCollapseClick(item.name as keyof StorageMenuCollapseProps)}>
            <ListItemIcon className={classes.listItemIcon}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
            {openCollapse[item.name as keyof StorageMenuCollapseProps] ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          <Collapse in={openCollapse[item.name as keyof StorageMenuCollapseProps]} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.subItems.map((sub: any) => renderMenuItem(sub, true))}
            </List>
          </Collapse>
        </React.Fragment>
      );
    }

    return (
      <ListItem
        button
        component={Link}
        to={item.state ? { pathname: item.link, state: item.state() } : item.link || '#'}
        key={item.text}
        className={clsx(
          isSubItem && classes.subItem,
          isSelected && classes.selectedItem
        )}
      >
        <ListItemIcon className={classes.listItemIcon}>{item.icon}</ListItemIcon>
        <ListItemText primary={item.text} />
      </ListItem>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <div className={classes.root}>
        <CssBaseline />
        <AppBar
          position="fixed"
          elevation={1}
          className={clsx(classes.appBar, { [classes.appBarShift]: open })}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleToggleDrawer}
              edge="start"
              className={clsx(classes.menuButton, open && classes.hide)}
            >
              <MenuIcon />
            </IconButton>
            <Box flexGrow={1} />
            <div className={classes.headerIcons}>
              {/* <IconButton color="inherit">
                <Badge badgeContent={4} color="secondary">
                  <ChatBubble />
                </Badge>
              </IconButton>
              <IconButton color="inherit">
                <Badge badgeContent={10} color="secondary">
                  <Notifications />
                </Badge>
              </IconButton> */}
              <Button
                onClick={handleMenuClick}
                className={classes.userButton}
                title={user.nome}
              />
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem disabled>Nível: {user.tipo_login === 'adm' ? 'Administrador' : 'Funcionário'}</MenuItem>
                {/* <MenuItem onClick={handleMenuClose}>Configurações</MenuItem> */}
                <MenuItem onClick={signOut}>Sair</MenuItem>
              </Menu>
            </div>
          </Toolbar>
        </AppBar>

        <Drawer
          variant="permanent"
          className={clsx(classes.drawer, {
            [classes.drawerOpen]: open,
            [classes.drawerClose]: !open,
          })}
          classes={{
            paper: clsx({
              [classes.drawerOpen]: open,
              [classes.drawerClose]: !open,
            }),
          }}
        >
          <div className={classes.toolbar}>
            <IconButton onClick={() => setOpen(false)}>
              <ChevronLeft style={{ color: '#FFF' }} />
            </IconButton>
          </div>
          <div className={classes.logoContainer}>
            <img
              src={prancheta3}
              alt="Logo"
              className={classes.logo}
              style={{ width: open ? 120 : 50 }}
            />
          </div>
          <List>{menuItems.map((item) => renderMenuItem(item))}</List>
        </Drawer>

        <main className={classes.content}>
          <div className={classes.toolbar} />
          <Router />
        </main>
      </div>
    </ThemeProvider>
  );
};
